import './styles.scss';

import { faCheck, faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ListItemText, Menu, MenuItem } from '@mui/material';
import React, { ReactNode, useContext, useMemo, useState, useEffect } from 'react';

import { IMMERSIVE_LIVE_ID } from '../../../../providers/otc-trader/quick-pricer-provider';
import { SurfaceMarkingContext } from '../../../../providers/otc-trader/surface-marking-provider';
import Utils from '../../../../utils/immersive_utils';

import { SearchInput } from './search-input';
import { filterByTime, TimeFilters } from './time-filters';

export interface IListUserVolSurfacesBySymbolVols {
  tenor: string;
  tag: string;
  value: number;
}

export interface IListUserVolSurfacesBySymbol {
  id: number | string;
  organisation: string;
  source: string;
  symbol: string;
  tag: string;
  timestamp: string;
  user: string;
  vols?: IListUserVolSurfacesBySymbolVols[];
  isLiveData?: boolean;
}

interface IFilteredListProperties {
  values: IListUserVolSurfacesBySymbol[];
  onChange: (value: any) => void;
  className?: string;
  label?: string;
  customLabel?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  resetSelected?: boolean;
  isQuickPricer?: boolean;
  defaultValue?: IListUserVolSurfacesBySymbol;
}

const LIVE_DATA_ITEM = {
  id: IMMERSIVE_LIVE_ID,
  organisation: '',
  source: '',
  symbol: '',
  tag: 'IMMERSIVE - LIVE',
  timestamp: '',
  user: '',
  isLiveData: true,
};

const DATE_FORMAT = 'HH:mm - DD MMM YYYY';

export default function FilteredList({
  values,
  onChange,
  className,
  label,
  customLabel,
  placeholder = 'Select option',
  disabled = false,
  resetSelected = false,
  isQuickPricer = false,
  defaultValue,
}: IFilteredListProperties) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { volatilityData, volatilityOptions, surfaceLoading } = useContext(SurfaceMarkingContext);

  const [selected, setSelected] = useState<IListUserVolSurfacesBySymbol | null>(
    defaultValue || (isQuickPricer ? LIVE_DATA_ITEM : null),
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTime, setSelectedTime] = useState('All');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (value: IListUserVolSurfacesBySymbol) => {
    setSelected(value);
    onChange(value);
    handleClose();
  };

  const makeDateFormat = (date: string) => {
    return Utils.formatDateUtc(date, DATE_FORMAT);
  };

  useEffect(() => {
    if (isQuickPricer) return;

    if (surfaceLoading || volatilityData.listUserVolSurfacesBySymbol.length === 0) return;

    if (volatilityOptions.surface && Object.keys(volatilityOptions.surface).length > 0) {
      setSelected(volatilityOptions.surface);
      onChange(volatilityOptions.surface);
    }
  }, [volatilityData.listUserVolSurfacesBySymbol]);

  useEffect(() => {
    if (!isQuickPricer) return;

    if (!values || values.length === 0) {
      setSelected(null);

      return;
    }

    if (!selected && !defaultValue) {
      if (values.find((v) => v.id === LIVE_DATA_ITEM.id)) {
        setSelected(LIVE_DATA_ITEM);

        return;
      }
    }

    if (defaultValue) {
      setSelected(defaultValue);
    }
  }, [values]);

  useEffect(() => {
    if (isQuickPricer && defaultValue && defaultValue !== selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue, isQuickPricer]);

  const getColor = (level: number) => {
    const colors = ['#1D2F39', '#172730', '#102028'];

    if (level <= colors.length) {
      return colors[level];
    }

    return 'red';
  };

  const renderItemQuickPricer = (item: IListUserVolSurfacesBySymbol, level: number) => {
    const formattedDate = makeDateFormat(item.timestamp);

    const label = item.isLiveData ? item.tag : `${formattedDate} | ${item.tag}`;

    return (
      <MenuItem
        key={item.id}
        selected={selected?.id === item.id}
        onClick={() => handleChange(item)}
        sx={{
          backgroundColor: getColor(level),
          paddingLeft: `${(level + 1) * 16}px`,
          paddingY: '10px',
        }}
      >
        <ListItemText>
          <div className="item-details">
            <div className="item-header">
              <span className="item-timestamp">{label}</span>
              {selected?.id === item.id && (
                <span className="selected-check-icon">
                  <FontAwesomeIcon icon={faCheck} className="icon" />
                </span>
              )}
            </div>
          </div>
        </ListItemText>
      </MenuItem>
    );
  };

  const renderItem = (item: IListUserVolSurfacesBySymbol, level: number) => {
    const formatUserNameFromEmail = (email: string): string => {
      if (!email) return '';

      const [username] = email.split('@');
      const nameParts = username.split('.');

      const formattedName = nameParts
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

      return formattedName;
    };

    return (
      <MenuItem
        key={item.id}
        selected={selected?.id === item.id}
        onClick={() => handleChange(item)}
        sx={{
          backgroundColor: getColor(level),
          paddingLeft: `${(level + 1) * 16}px`,
          paddingY: '10px',
        }}
      >
        <ListItemText>
          <div className="item-details">
            <div className="item-header">
              <span className="item-timestamp">
                {makeDateFormat(item.timestamp)}
                {' | '}
                {item.tag}
              </span>
              {selected?.id === item.id && (
                <span className="selected-check-icon">
                  <FontAwesomeIcon icon={faCheck} className="icon" />
                </span>
              )}
            </div>
            <div className="item-subheader">
              <span className="item-symbol">
                {item.symbol} • {item.organisation} • {formatUserNameFromEmail(item.user)}
              </span>
            </div>
          </div>
        </ListItemText>
      </MenuItem>
    );
  };

  const filteredValues = useMemo(() => {
    if (!values) return [];

    const filteredBySearch = values.filter(
      (item) =>
        item.tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.timestamp?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.user?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.organisation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.symbol?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    if (isQuickPricer) {
      return filteredBySearch;
    }

    return filterByTime(filteredBySearch, selectedTime);
  }, [values, searchTerm, selectedTime, volatilityData, isQuickPricer]);

  const renderLabel = (selected: IListUserVolSurfacesBySymbol) => {
    if (selected.isLiveData) {
      return selected.tag;
    }

    return `${selected.tag} | ${makeDateFormat(selected.timestamp)}`;
  };

  useEffect(() => {
    if (resetSelected) {
      setSelected(null);
    }
  }, [resetSelected]);

  return (
    <div className={`select-container ${className}`}>
      {label && <span className="label">{label}</span>}
      <Button
        onClick={handleClick}
        aria-controls={open ? 'menu' : undefined}
        aria-haspopup="true"
        disableRipple
        aria-expanded={open ? 'true' : undefined}
        variant="text"
        className={open ? 'selector-button selector-button-open ' : ' selector-button'}
        endIcon={
          open ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />
        }
      >
        <span className={`select-name ${!selected ? 'filtered-placeholder' : ''}`}>
          {customLabel || (selected ? renderLabel(selected) : placeholder)}{' '}
        </span>
      </Button>

      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className="filtered-select-menu"
      >
        <SearchInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        {!isQuickPricer && (
          <TimeFilters selectedTime={selectedTime} setSelectedTime={setSelectedTime} />
        )}
        <div className="filtered-select-menu-list">
          {filteredValues.map((item) =>
            isQuickPricer ? renderItemQuickPricer(item, 0) : renderItem(item, 0),
          )}
        </div>
        {filteredValues.length === 0 && (
          <div className="filtered-not-results">No results found</div>
        )}
      </Menu>
    </div>
  );
}
